/**
 * LandingPage — Public marketing page for Searchline
 * Route: /landing (no auth required)
 *
 * Nova rewrite — May 2026
 * Changes: tighter hero copy, impact stat callout, social proof strip,
 *          before/after comparison section, sharpened value props
 */

import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import searchlineLogo from '@/assets/searchline-logo.jpg'

// ── Erica glowing circle ──────────────────────────────────────────────────────
function EricaCircle() {
  const barConfigs = [
    { keyframe: 'lp-bar-1', delay: '0s',    minH: 5,  maxH: 22 },
    { keyframe: 'lp-bar-2', delay: '0.1s',  minH: 8,  maxH: 28 },
    { keyframe: 'lp-bar-3', delay: '0.05s', minH: 6,  maxH: 32 },
    { keyframe: 'lp-bar-4', delay: '0.15s', minH: 8,  maxH: 24 },
    { keyframe: 'lp-bar-5', delay: '0.08s', minH: 5,  maxH: 18 },
  ]
  return (
    <>
      <style>{`
        @keyframes lp-bar-1 { 0%,100% { height: 5px; }  50% { height: 22px; } }
        @keyframes lp-bar-2 { 0%,100% { height: 8px; }  50% { height: 28px; } }
        @keyframes lp-bar-3 { 0%,100% { height: 6px; }  50% { height: 32px; } }
        @keyframes lp-bar-4 { 0%,100% { height: 8px; }  50% { height: 24px; } }
        @keyframes lp-bar-5 { 0%,100% { height: 5px; }  50% { height: 18px; } }
      `}</style>
      <div className="relative flex items-center justify-center w-40 h-40">
        <div className="absolute inset-0 rounded-full bg-[#FD802E]/30 border-4 border-[#FD802E]/70 shadow-[0_0_40px_rgba(253,128,46,0.5)] animate-pulse" />
        <div className="absolute w-32 h-32 rounded-full bg-[#FD802E]/40 border-2 border-[#FD802E]/80" />
        <div className="relative w-20 h-20 rounded-full bg-[#02182B] flex items-center justify-center shadow-lg shadow-[#FD802E]/40">
          <div className="flex items-center gap-[3px]">
            {barConfigs.map((cfg, i) => (
              <div
                key={i}
                className="w-[4px] rounded-full bg-[#FD802E]"
                style={{ animationName: cfg.keyframe, animationDuration: ['0.8s','0.6s','0.7s','0.9s','0.75s'][i], animationTimingFunction: 'ease-in-out', animationIterationCount: 'infinite', animationDelay: cfg.delay, height: `${cfg.minH}px` }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

// ── Animated pipeline demo ────────────────────────────────────────────────────
const ALL_CANDIDATES = [
  { name: 'Alexandra Chen', title: `VP Engineering @ Stripe',     stage: 'Shortlist`,   score: 'Strong Fit',  scoreColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { name: 'Marcus Okafor',  title: `CTO @ Series B Fintech',      stage: 'Pre-Screen`,  score: 'Strong Fit',  scoreColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { name: 'Priya Sharma',   title: `Eng Director @ Plaid',        stage: 'Shortlist`,   score: 'Strong Fit',  scoreColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { name: 'David Reinholt', title: `Head of Product @ Notion',    stage: 'Outreach`,    score: 'Review',      scoreColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { name: 'Sofia Mendez',   title: `Principal Eng @ Vercel',      stage: 'Pre-Screen`,  score: 'Strong Fit',  scoreColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { name: 'James Park',     title: `Staff Engineer @ Linear',     stage: 'Identified`,  score: 'Potential',   scoreColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
]

const stageColor: Record<string, string> = {
  'Identified': 'bg-slate-600/40 text-slate-300 border-slate-500/30',
  'Outreach':   'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'Pre-Screen': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'Shortlist':  'bg-[#FD802E]/20 text-[#FD802E] border-[#FD802E]/30',
}

function PipelineMockup() {
  const [visibleCount, setVisibleCount] = useState(3)
  const [tick, setTick]   = useState(0)

  // Every 2.5s, reveal a new row to simulate Erica working
  useEffect(() => {
    if (visibleCount >= ALL_CANDIDATES.length) return
    const t = setTimeout(() => {
      setVisibleCount((n) => n + 1)
      setTick((n) => n + 1)
    }, 2500)
    return () => clearTimeout(t)
  }, [visibleCount, tick])

  const elapsed  = visibleCount * 2.5
  const timeStr  = elapsed < 60 ? `${Math.round(elapsed)}m` : `${(elapsed / 60).toFixed(1)}h`

  return (
    <div className="w-full max-w-2xl mx-auto rounded-2xl bg-[#0a2030] border border-white/10 shadow-2xl overflow-hidden">
      {/* Window chrome */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[#071820] border-b border-white/10">
        <div className="w-3 h-3 rounded-full bg-red-500/70" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
        <div className="w-3 h-3 rounded-full bg-green-500/70" />
        <span className="ml-3 text-xs text-white/30 font-mono">Candidate Pipeline — VP Engineering, London</span>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-[#FD802E] animate-pulse" />
          <span className="text-xs text-[#FD802E]/70 font-mono">Erica working · {timeStr}</span>
        </div>
      </div>
      {/* Header bar */}
      <div className="flex items-center gap-4 px-4 py-3 bg-[#071820]/60 border-b border-white/5 text-xs text-white/40 font-medium">
        <span className="w-48">Candidate</span>
        <span className="flex-1 hidden sm:block">Current Role</span>
        <span className="w-28 text-center">Stage</span>
        <span className="w-24 text-center">Score</span>
      </div>
      {/* Rows */}
      {ALL_CANDIDATES.slice(0, visibleCount).map((c, i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-all duration-500"
          style={{ animation: i === visibleCount - 1 ? 'fadeSlideIn 0.4s ease-out' : undefined }}
        >
          <div className="w-48 flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-full bg-[#233D4C] flex items-center justify-center text-xs font-semibold text-[#FD802E] shrink-0">
              {c.name.charAt(0)}
            </div>
            <span className="text-sm text-white font-medium truncate">{c.name}</span>
          </div>
          <div className="flex-1 hidden sm:block">
            <span className="text-xs text-white/50 truncate">{c.title}</span>
          </div>
          <div className="w-28 flex justify-center">
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${stageColor[c.stage]}`}>
              {c.stage}
            </span>
          </div>
          <div className="w-24 flex justify-center">
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${c.scoreColor}`}>
              {c.score}
            </span>
          </div>
        </div>
      ))}
      {/* Footer summary */}
      <div className="flex items-center gap-6 px-4 py-3 bg-[#071820]/60 text-xs text-white/30">
        <span>{visibleCount * 4} candidates sourced</span>
        <span>·</span>
        <span className="text-[#FD802E]">{ALL_CANDIDATES.slice(0, visibleCount).filter(c => c.stage === 'Shortlist').length} shortlisted</span>
        <span>·</span>
        <span>{ALL_CANDIDATES.slice(0, visibleCount).filter(c => ['Pre-Screen', 'Shortlist'].includes(c.stage)).length} pre-screened</span>
        {visibleCount < ALL_CANDIDATES.length && (
          <>
            <span>·</span>
            <span className="text-[#FD802E] animate-pulse">Erica screening…</span>
          </>
        )}
      </div>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

// ── Demo request form ─────────────────────────────────────────────────────────
function DemoForm() {
  const [form, setForm] = useState({ name: '', email: '', company: '' })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setFormError(null)
    try {
      const base = (import.meta as unknown as { env: Record<string, string> }).env.VITE_ENGINE_API_BASE_URL ?? ''
      const res = await fetch(`${base}/api/engine/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? 'Something went wrong')
      }
      setSubmitted(true)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="text-center py-10">
        <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-white text-xl font-semibold">You're in{form.name ? `, ${form.name}` : ''}.</p>
        <p className="text-white/50 mt-1">We'll be in touch within 24 hours to set up your live demo.</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <input
        type="text"
        required
        placeholder="Your name"
        value={form.name}
        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        className="w-full rounded-xl bg-[#02182B] border border-white/10 text-white placeholder-white/30 px-4 py-3 text-sm focus:outline-none focus:border-[#FD802E]/60 transition-colors"
      />
      <input
        type="email"
        required
        placeholder="Work email"
        value={form.email}
        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
        className="w-full rounded-xl bg-[#02182B] border border-white/10 text-white placeholder-white/30 px-4 py-3 text-sm focus:outline-none focus:border-[#FD802E]/60 transition-colors"
      />
      <input
        type="text"
        required
        placeholder="Company"
        value={form.company}
        onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
        className="w-full rounded-xl bg-[#02182B] border border-white/10 text-white placeholder-white/30 px-4 py-3 text-sm focus:outline-none focus:border-[#FD802E]/60 transition-colors"
      />
      {formError && (
        <p className="text-red-400 text-sm text-center">{formError}</p>
      )}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-[#FD802E] hover:bg-[#e8712a] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors text-sm mt-1"
      >
        {loading ? 'Sending…' : 'Book a 30-minute live demo →'}
      </button>
      <p className="text-center text-white/25 text-xs">No commitment. We'll run a live search from brief to shortlist in the session.</p>
    </form>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export function LandingPage() {
  const howItWorksRef = useRef<HTMLElement>(null)
  const demoRef       = useRef<HTMLElement>(null)

  const scrollTo = (ref: React.RefObject<HTMLElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="min-h-screen bg-[#02182B] text-white font-sans">

      {/* ── 1. Navigation ───────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-[#02182B]/90 backdrop-blur border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={searchlineLogo} alt="Searchline" className="w-8 h-8 rounded-lg" />
            <div>
              <span className="text-white font-bold text-lg tracking-tight">Searchline</span>
              <span className="block text-[#FD802E] font-medium text-xs">AI Hiring</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/blog"
              className="hidden sm:block text-white/50 hover:text-white text-sm transition-colors"
            >
              Blog
            </Link>
            <button
              onClick={() => scrollTo(howItWorksRef)}
              className="hidden sm:block text-white/50 hover:text-white text-sm transition-colors"
            >
              How it works
            </button>
            <button
              onClick={() => scrollTo(demoRef)}
              className="bg-[#FD802E] hover:bg-[#e8712a] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              Request a Demo
            </button>
          </div>
        </div>
      </nav>

      {/* ── 2. Hero ─────────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-5 pt-20 pb-16 lg:pt-28 lg:pb-24">
        <div className="flex flex-col items-center text-center gap-8">

          <div className="inline-flex items-center gap-2 bg-[#FD802E]/10 border border-[#FD802E]/20 rounded-full px-4 py-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#FD802E] animate-pulse" />
            <span className="text-[#FD802E] text-xs font-medium">Powered by Erica — AI Recruitment Agent</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight max-w-3xl">
            You only conduct<br className="hidden sm:block" />{' '}
            the <span className="text-[#FD802E]">final interviews.</span>
          </h1>

          <p className="text-white/60 text-base sm:text-xl leading-relaxed max-w-2xl">
            Brief Erica in 20 minutes. She sources, outreaches, and pre-screens every candidate.
            You get a ranked shortlist of people worth interviewing — usually in under 4 hours.
          </p>

          {/* Impact callout */}
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 bg-[#233D4C]/60 border border-white/5 rounded-2xl px-7 py-4">
            {[
              { n: '< 4h',    label: 'First shortlist delivered' },
              { n: '40+ hrs', label: 'Saved per search' },
              { n: '100%',    label: 'Candidates pre-screened' },
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center sm:items-start gap-0.5">
                <span className="text-2xl font-bold text-[#FD802E]">{stat.n}</span>
                <span className="text-white/45 text-xs">{stat.label}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <button
              onClick={() => scrollTo(demoRef)}
              className="bg-[#FD802E] hover:bg-[#e8712a] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors text-sm"
            >
              Book a Live Demo →
            </button>
            <button
              onClick={() => scrollTo(howItWorksRef)}
              className="border border-white/20 hover:border-white/40 text-white/80 hover:text-white font-semibold px-7 py-3.5 rounded-xl transition-colors text-sm"
            >
              See how it works ↓
            </button>
          </div>

          {/* Animated pipeline mockup */}
          <div className="w-full mt-4">
            <p className="text-white/25 text-xs mb-4">Watch Erica build a shortlist in real time ↓</p>
            <PipelineMockup />
          </div>
        </div>
      </section>

      {/* ── 3. Trust strip ──────────────────────────────────────────────────── */}
      <section className="border-y border-white/5 bg-[#233D4C]/20">
        <div className="max-w-6xl mx-auto px-5 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 text-center">
            <p className="text-white/30 text-xs font-medium uppercase tracking-widest shrink-0">Built by executive search professionals for</p>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              {['Executive Search Firms', 'In-house Talent Teams', 'Retained Search', 'RPO Providers'].map((label) => (
                <span key={label} className="text-white/50 text-sm font-medium">{label}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Before / After ───────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold">What changes when you use Searchline</h2>
          <p className="text-white/50 mt-3 text-base max-w-xl mx-auto">Every hour you currently spend on early-stage hiring gets handed to Erica.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Before */}
          <div className="rounded-2xl border border-red-500/10 bg-red-500/5 p-7">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <span className="text-red-400 text-xs font-bold">✕</span>
              </div>
              <span className="text-red-400 font-semibold text-sm">Without Searchline</span>
            </div>
            <ul className="space-y-3">
              {[
                'Spend 2–3 days manually sourcing on LinkedIn',
                'Write and send the same outreach template to 50 people',
                'Chase non-responders across email and LinkedIn',
                'Block out 3 hours to run first-round phone screens',
                'Shortlist ready in 4–6 weeks. Some roles longer.',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-white/50">
                  <span className="text-red-500/60 mt-0.5 shrink-0">—</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* After */}
          <div className="rounded-2xl border border-[#FD802E]/20 bg-[#FD802E]/5 p-7">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-6 h-6 rounded-full bg-[#FD802E]/20 flex items-center justify-center">
                <span className="text-[#FD802E] text-xs font-bold">✓</span>
              </div>
              <span className="text-[#FD802E] font-semibold text-sm">With Searchline</span>
            </div>
            <ul className="space-y-3">
              {[
                '20 minutes with Erica to capture your full brief',
                'Erica sources and scores candidates automatically',
                'Personalised outreach sent to every candidate',
                'AI pre-screen interviews run 24/7, no scheduling needed',
                'Ranked shortlist with full transcripts delivered in hours.',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-white/70">
                  <span className="text-[#FD802E] mt-0.5 shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── 4b. ATS Integrations ─────────────────────────────────────────────── */}
      <section className="border-y border-white/5 bg-[#071820]">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold">Works with your existing ATS</h2>
            <p className="text-white/50 mt-3 text-sm sm:text-base max-w-2xl mx-auto">
              Searchline sits as a layer on top of your current setup via Make.com and Zapier. Your ATS stays untouched.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {[
              'Greenhouse', 'Lever', 'Workday', 'SmartRecruiters', 'Teamtailor',
              'Ashby', 'BambooHR', 'JazzHR', 'Breezy HR', 'Rippling',
              'Oracle HCM', 'SAP SuccessFactors',
            ].map((ats) => (
              <span
                key={ats}
                className="px-4 py-2 rounded-full border border-white/15 bg-white/5 text-white/70 text-sm font-medium hover:border-white/30 hover:text-white transition-colors"
              >
                {ats}
              </span>
            ))}
          </div>
          <p className="text-center text-white/30 text-sm mt-8">
            Don't see yours? If it has an API, we can connect it.
          </p>
        </div>
      </section>

      {/* ── 5. How it works ─────────────────────────────────────────────────── */}
      <section
        ref={howItWorksRef}
        id="how-it-works"
        className="bg-[#233D4C]/20 border-y border-white/5"
      >
        <div className="max-w-6xl mx-auto px-5 py-20">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold">Three steps. Weeks of work in hours.</h2>
            <p className="text-white/50 mt-3 text-base max-w-xl mx-auto">You stay in control. Erica does the heavy lifting.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                n: '01',
                icon: '💬',
                title: 'Brief Erica (20 mins)',
                body: `Talk to Erica like you'd brief a trusted colleague. Upload the job spec, share context on the company and culture, tell her what's failed before. She asks smart follow-up questions until she has everything she needs.`,
              },
              {
                n: '02',
                icon: '🤖',
                title: 'Erica works 24/7',
                body: 'Erica searches for candidates who match your brief, writes personalised outreach for each one, and runs async AI pre-screen interviews — no scheduling, no back-and-forth. She never stops until she has your shortlist.',
              },
              {
                n: '03',
                icon: '📋',
                title: 'You receive the shortlist',
                body: `Every candidate comes with Erica\'s full assessment: score, strengths, areas to probe, and the complete pre-screen transcript. You only spend time on people who\'ve already been properly vetted.`,
              },
            ].map((step) => (
              <div
                key={step.n}
                className="bg-[#233D4C] rounded-2xl p-7 border border-white/5 flex flex-col gap-4"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#FD802E] flex items-center justify-center text-sm font-bold text-white shrink-0">
                    {step.n}
                  </div>
                  <span className="text-2xl">{step.icon}</span>
                </div>
                <h3 className="text-lg font-semibold">{step.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Value props grid ──────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold">Everything serious hiring needs</h2>
          <p className="text-white/50 mt-3 text-base max-w-xl mx-auto">Nothing bolted on. No shortcuts. Built for quality outcomes.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            {
              icon: '🎯',
              title: 'Only the best reach your desk',
              body: 'Erica pre-screens every candidate before you see them. No more wading through CVs that don\'t fit the brief.',
            },
            {
              icon: '✉️',
              title: 'Personalised outreach at scale',
              body: 'Each candidate gets an email written specifically for them — their background, their career, your role. Not a blast. A conversation starter.',
            },
            {
              icon: '🌊',
              title: 'Works around the clock',
              body: 'Erica runs pre-screens 24/7. Candidates complete them in their own time. Your pipeline moves while you sleep.',
            },
            {
              icon: '📊',
              title: 'Full funnel visibility',
              body: 'See exactly where every candidate sits — opened, clicked, pre-screened, shortlisted. No black box. No guessing.',
            },
            {
              icon: '🔗',
              title: 'Integrates with your ATS',
              body: 'Searchline sits on top of your existing setup. Candidates flow in, pre-screened shortlists flow out. Zero workflow disruption.',
            },
            {
              icon: '📝',
              title: 'Full interview transcripts',
              body: 'Every pre-screen delivers a complete Q&A transcript with Erica\'s notes. You know exactly what was said before the call.',
            },
          ].map((card) => (
            <div
              key={card.title}
              className="bg-[#233D4C] rounded-2xl p-6 border border-white/5 hover:border-[#FD802E]/20 transition-colors"
            >
              <span className="text-2xl">{card.icon}</span>
              <h3 className="text-base font-semibold mt-3 mb-2">{card.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{card.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 7. ATS integration ──────────────────────────────────────────────── */}
      <section className="bg-[#233D4C]/20 border-y border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <div className="bg-[#233D4C] rounded-2xl border border-white/5 p-8 sm:p-12">
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl font-bold">Already have an ATS? Good.</h2>
              <p className="text-white/55 mt-4 text-base leading-relaxed">
                Searchline doesn't replace your ATS — it makes it smarter. Candidates apply through your existing system. Searchline handles early-stage work: sourcing, scoring, outreach, and AI pre-screening. Your ATS receives a pre-qualified shortlist. The rest of your process stays exactly the same.
              </p>
            </div>

            {/* Flow diagram */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-2 flex-wrap">
              {[
                { label: `Your ATS', sub: 'Receives applications`, color: 'border-white/20 text-white/70' },
                null,
                { label: `Searchline', sub: 'Sources · Scores · Screens`, color: 'border-[#FD802E]/40 text-[#FD802E]', highlight: true },
                null,
                { label: `Pre-screened shortlist', sub: 'Ranked & assessed`, color: 'border-emerald-500/40 text-emerald-400' },
                null,
                { label: `Your ATS', sub: 'Receives shortlist`, color: 'border-white/20 text-white/70' },
              ].map((item, i) => {
                if (item === null) {
                  return <div key={i} className="text-white/30 text-xl font-light hidden sm:block">→</div>
                }
                return (
                  <div
                    key={i}
                    className={`rounded-xl border px-5 py-3 text-center min-w-[130px] ${item.highlight ? 'bg-[#FD802E]/10' : 'bg-[#02182B]/60'} ${item.color}`}
                  >
                    <p className="text-sm font-semibold">{item.label}</p>
                    <p className={`text-xs mt-0.5 ${item.highlight ? 'text-[#FD802E]/70' : 'text-white/30'}`}>{item.sub}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. Meet Erica ───────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="flex flex-col items-center lg:items-start gap-6 lg:w-1/2">
            <EricaCircle />
            <div>
              <h2 className="text-3xl font-bold text-center lg:text-left">Meet Erica</h2>
              <p className="text-white/55 mt-4 text-base leading-relaxed text-center lg:text-left max-w-md">
                Erica is Searchline's AI recruitment agent. She runs your intake, sources candidates, writes outreach, and conducts pre-screen interviews — all on her own. She's not a chatbot. She's the researcher, sourcer, and screener you always needed but couldn't afford to hire.
              </p>
            </div>
            <div className="flex items-center gap-2 text-white/30 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-[#FD802E]" />
              <span>Powered by Searchline AI · Available 24/7</span>
            </div>
          </div>

          {/* Erica capability list */}
          <div className="lg:w-1/2 w-full">
            <div className="bg-[#233D4C] rounded-2xl border border-white/5 p-6 space-y-3">
              <p className="text-white/30 text-xs font-medium uppercase tracking-wide mb-4">What Erica does for you</p>
              {[
                { icon: '📥', text: 'Runs your intake conversation to capture the full brief' },
                { icon: '🔍', text: 'Searches LinkedIn, databases, and networks for matching candidates' },
                { icon: '📊', text: 'Scores and ranks every candidate against your criteria' },
                { icon: '✉️', text: 'Writes and sends personalised outreach to every candidate' },
                { icon: '🎤', text: 'Conducts structured pre-screen interviews over async chat' },
                { icon: '📋', text: 'Delivers a ranked shortlist with full transcripts and assessments' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <span className="text-lg shrink-0">{item.icon}</span>
                  <span className="text-white/70 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 9. Request Demo ─────────────────────────────────────────────────── */}
      <section ref={demoRef} id="demo" className="bg-[#233D4C]/20 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold">See it work on a real search</h2>
              <p className="text-white/55 mt-3 text-base">
                We'll run a live search with Erica during your demo — from brief to shortlist in 30 minutes.
              </p>
              <div className="flex items-center justify-center gap-4 mt-4 text-xs text-white/30">
                <span>✓ No slides</span>
                <span>·</span>
                <span>✓ No commitment</span>
                <span>·</span>
                <span>✓ Your actual role, your brief</span>
              </div>
            </div>
            <div className="bg-[#233D4C] rounded-2xl border border-white/5 p-8">
              <DemoForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── 10. Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/5 bg-[#02182B]">
        <div className="max-w-6xl mx-auto px-5 py-10 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="text-center sm:text-left">
            <div className="flex items-center gap-2">
              <img src={searchlineLogo} alt="Searchline" className="w-7 h-7 rounded-lg" />
              <span className="text-white font-bold text-base tracking-tight">Searchline</span>
            </div>
            <p className="text-white/30 text-xs mt-2">The AI recruitment agent that works while you sleep.</p>
          </div>
          <div className="flex flex-col items-center sm:items-end gap-2">
            <div className="flex items-center gap-4 text-xs text-white/30">
              <a href="#" className="hover:text-white/60 transition-colors">Privacy Policy</a>
              <span>·</span>
              <a href="#" className="hover:text-white/60 transition-colors">Terms of Service</a>
            </div>
            <p className="text-white/20 text-xs">© 2026 Searchline. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
  )
}
